import { SidebarMenu, SidebarProvider } from '@monksflow/monks-ui';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';

import { usePermissions } from '@/entities/user';
import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { ANALYSIS_SECTIONS, SIDEBAR_WIDTH } from './AnalysisSidebar.constants';
import type { AnalysisSidebarProps } from './AnalysisSidebar.types';
import { useAnalysisSectionNav } from './useAnalysisSectionNav';
import { AnalysisSidebarNavItem } from './AnalysisSidebarNavItem';
import { AnalysisRailToggle } from './AnalysisRailToggle';

/**
 * Redesigned IQ rail: a section list with no analysis selector, collapsing to
 * zero width from a `panel-left` button in its own header. Collapsed state is
 * owned by the page shell, which renders the re-open button in the content area
 * once the rail has no width left to hold one.
 *
 * A plain `aside` animating its own width, not the DS `Sidebar`: that primitive's
 * `offcanvas` mode positions the rail `fixed` against the viewport for a
 * full-height app shell, which misplaces a rail nested inside the content
 * surface. The inner box holds a fixed width so labels don't reflow while the
 * rail animates shut.
 *
 * `SidebarProvider` still wraps the rows because `SidebarMenuButton` reads its
 * context and throws without one. It stays UNCONTROLLED: collapse is this
 * component's own width animation, so handing the provider our `open` would give
 * the same state two owners.
 */
export const AnalysisSidebar = ({ analysisId, open, onClose }: AnalysisSidebarProps) => {
  const { t } = useTranslation('analysis');
  const { selectedSection, handleSectionClick } = useAnalysisSectionNav(analysisId);
  const shouldReduceMotion = useReducedMotion();

  // Same per-section gates the original rail applies, so a section hidden there
  // is hidden here too rather than becoming reachable only under the redesign.
  //
  // YouGov and Pathmatics are licensed data, so their sections need the provider's own
  // permission on top of the flag. The route guards repeat the check: hiding a rail entry
  // does not stop someone reaching the section by URL.
  const { canAccessYougov, canAccessPathmatics } = usePermissions();
  const isYougovEnabled = useBooleanFlagValue('iq_yougov', false) && canAccessYougov;
  const isPathmaticsEnabled = useBooleanFlagValue('iq_pathmatics', false) && canAccessPathmatics;
  const hiddenSectionIds = new Set(
    (
      [
        isYougovEnabled ? null : ROUTE_PATTERNS.IQ_SECTION_MARKETING_PERFORMANCE,
        isPathmaticsEnabled ? null : ROUTE_PATTERNS.IQ_SECTION_MEDIA_LANDSCAPE,
        isPathmaticsEnabled ? null : ROUTE_PATTERNS.IQ_SECTION_CREATIVE_LANDSCAPE,
      ] as Array<string | null>
    ).filter((id): id is string => id !== null),
  );
  const sections = ANALYSIS_SECTIONS.filter((section) => !hiddenSectionIds.has(section.id));

  return (
    <aside
      data-testid="analysis-sidebar-v2"
      inert={!open}
      // Resizing while staying on screen, so ease-out in both directions.
      className={`h-full shrink-0 overflow-hidden border-r transition-[width,border-color] duration-base ease-out ${
        open ? 'border-border-lighter dark:border-border-light/50' : 'border-transparent'
      }`}
      style={{ width: open ? SIDEBAR_WIDTH : 0 }}
    >
      <div
        className="flex h-full flex-col gap-3 overflow-auto p-3"
        style={{ width: SIDEBAR_WIDTH }}
      >
        <div className="flex items-center justify-between">
          <span className="text-base font-medium text-foreground">
            {t('analysis.analysisSidebar.categories')}
          </span>
          <AnalysisRailToggle
            label={t('analysis.analysisSidebar.closeNavigation')}
            onClick={onClose}
          />
        </div>

        {/* `min-h-0!` cancels the provider wrapper's own `min-h-svh`, which would
            otherwise stretch the row list to the full viewport height. */}
        <SidebarProvider
          className="w-full flex-1 min-h-0!"
          style={{ '--sidebar-width': '100%' } as React.CSSProperties}
        >
          <motion.nav
            aria-label={t('analysis.analysisSidebar.sectionNavigation')}
            className="flex w-full flex-col gap-1"
            variants={
              shouldReduceMotion
                ? undefined
                : {
                    hidden: {},
                    // Same designer-tuned stagger the shared FeatureSidebar plays.
                    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
                  }
            }
            initial={shouldReduceMotion ? false : 'hidden'}
            animate="visible"
          >
            <SidebarMenu className="gap-1">
              {sections.map((section) => (
                <AnalysisSidebarNavItem
                  key={section.id}
                  icon={section.icon}
                  label={section.label}
                  active={selectedSection === section.id}
                  onClick={() => handleSectionClick(section.id)}
                />
              ))}
            </SidebarMenu>
          </motion.nav>
        </SidebarProvider>
      </div>
    </aside>
  );
};
